import { SlashMenue } from "./slash-menue";

/*
 * Inhaltsstile und Optionen der Editor-Einfassung (Bauplan E2/E3). Die Klassen hängen als `HTMLAttributes`-artige
 * Darstellungs-Attribute an den Knoten des Editors (wie im Preline-Block `rich-text-editor-card`), NICHT über
 * `@horn/editor/theme.css`: nur Theme-Tokens und Standardklassen, kein Roh-Hex, kein `style=`, kein `dark:`.
 * Ganze, feste Ketten (nie zusammengesetzt, R8). Das Dokument-Schema bleibt unberührt.
 */

/** Das bearbeitbare Element (ProseMirror-Wurzel): Mindesthöhe gegen Layoutsprung, Abstand zwischen den Blöcken. */
export const EDITOR_FLAECHE_KLASSE = "relative min-h-56 p-3 sm:p-4 space-y-3 focus:outline-hidden focus-visible:outline-0";

/**
 * Flächenhöhe (Abnahme Besprechungsprotokoll 19.10.2026, additiv): `standard` = wie bisher (Mindesthöhe 14 rem, Protokolltext, Notiz je Punkt),
 * `kompakt` = kurze Mindesthöhe für ein Feld in einem Formular (Beschreibung des Agenda-Punkts), `mitwachsend` = KEINE Mindesthöhe, die Fläche wächst
 * mit dem Text (lesende Anzeige kurzer Texte, z. B. Zusammenfassung in der Fassung). `buendig` (Besprechung-Seite neu, Slice 4, nur zusammen mit `rahmenlos` am
 * `EditorRahmen`): weder Mindesthöhe noch Innenabstand, der Text steht bündig in einer Karte, die schon Rahmen und Abstand trägt. Ganze, feste Ketten (R8).
 */
export type EditorFlaechenHoehe = "standard" | "kompakt" | "mitwachsend" | "buendig";
export const EDITOR_FLAECHE_KOMPAKT_KLASSE = "relative min-h-24 p-3 sm:p-4 space-y-3 focus:outline-hidden focus-visible:outline-0";
export const EDITOR_FLAECHE_MITWACHSEND_KLASSE = "relative p-3 sm:p-4 space-y-3 focus:outline-hidden focus-visible:outline-0";
export const EDITOR_FLAECHE_BUENDIG_KLASSE = "relative space-y-3 focus:outline-hidden focus-visible:outline-0";
export function editorFlaecheKlasse(hoehe: EditorFlaechenHoehe = "standard"): string {
  return hoehe === "kompakt"
    ? EDITOR_FLAECHE_KOMPAKT_KLASSE
    : hoehe === "mitwachsend"
      ? EDITOR_FLAECHE_MITWACHSEND_KLASSE
      : hoehe === "buendig"
        ? EDITOR_FLAECHE_BUENDIG_KLASSE
        : EDITOR_FLAECHE_KLASSE;
}

type KlassenWert = string | Partial<Record<number, string>>;

/** Klassen je Knoten- und Mark-Typ. Schlüssel = TipTap-Typnamen. */
export const EDITOR_KNOTEN_KLASSEN: Record<string, KlassenWert> = {
  paragraph: "text-sm text-foreground",
  heading: {
    1: "text-xl font-semibold text-foreground",
    2: "text-lg font-semibold text-foreground",
    3: "text-base font-semibold text-foreground",
    4: "text-sm font-semibold text-foreground",
    5: "text-sm font-semibold text-foreground",
    6: "text-sm font-semibold text-foreground",
  },
  bulletList: "list-disc list-outside ps-5 space-y-1 text-foreground",
  orderedList: "list-decimal list-outside ps-5 space-y-1 text-foreground",
  listItem: "ps-1 text-sm",
  taskList: "list-none ps-0 space-y-1",
  taskItem: "flex items-start gap-x-2 text-primary",
  blockquote: "relative border-s-4 border-layer-line ps-4 text-foreground",
  codeBlock: "bg-surface text-surface-foreground rounded-lg p-3 font-mono text-xs overflow-x-auto",
  code: "rounded-md bg-muted px-1 py-0.5 font-mono text-xs text-foreground",
  horizontalRule: "my-4 border-layer-line",
  link: "text-primary font-medium underline underline-offset-2 hover:text-primary-hover",
};

/**
 * Punkte des Slash-Menüs in der Einfassung (Betreiber 11.10.2026: „/“ bleibt, reduziert): Überschriften, Liste,
 * Nummerierung, Aufgabenliste, Zitat, Trennlinie. Nicht angeboten: Bild (`window.prompt`), Tabelle, Hinweisfelder,
 * Wiki-Bausteine, Code-Block, Datum. Das Schema kennt sie weiter, vorhandene Inhalte bleiben erhalten.
 */
export const EDITOR_SLASH_PUNKTE = ["h1", "h2", "h3", "list", "ordered", "checklist", "quote", "hr"] as const;

/**
 * Alle Optionen, die `useHornEditor` (`@horn/editor`) für die Preline-Hülle braucht; so aufrufen:
 * `useHornEditor({ value, onChange, readOnly, ...editorHuelleOptionen("Protokolltext") })`.
 * Gibt reine Daten und die Darstellung des Slash-Menüs zurück; das Paket hängt nicht von `@horn/editor` ab.
 */
export function editorHuelleOptionen(label: string, hoehe: EditorFlaechenHoehe = "standard") {
  return {
    editorKlasse: editorFlaecheKlasse(hoehe),
    editorAttribute: { "aria-label": label, "aria-multiline": "true" },
    knotenKlassen: EDITOR_KNOTEN_KLASSEN,
    wikiLinkSymbol: false,
    slash: { punkte: EDITOR_SLASH_PUNKTE, darstellung: SlashMenue, zIndex: "70" },
  } as const;
}
